import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { createCategoryApi, deleteCategoryApi, listCategoriesApi, updateCategoryApi } from "../api/financeiroApi";
import type { FinCategory, FinCategoryKind } from "../api/finTypes";

type Props = { token: string; canWrite: boolean };

/** Edição em modal: nova raiz (`kind`), nova filha (`parent`) ou renomear (`category`). */
type Editing =
  | { mode: "root"; kind: FinCategoryKind }
  | { mode: "child"; parent: FinCategory }
  | { mode: "rename"; category: FinCategory }
  | null;

const KIND_TITLES: Record<FinCategoryKind, string> = { RECEITA: "Receitas", DESPESA: "Despesas" };

/**
 * Plano de contas: grupos e subcategorias de receita e de despesa. Categoria de
 * sistema (cadeado) é usada pelas integrações com a folha e não se exclui.
 */
export default function CategoriesSettings({ token, canWrite }: Props) {
  const [tree, setTree] = useState<FinCategory[] | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<FinCategory | null>(null);

  const load = useCallback(() => {
    listCategoriesApi(token, { includeInactive: true })
      .then(setTree)
      .catch((error: Error) => toast.error(error.message));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (next: NonNullable<Editing>) => {
    setEditing(next);
    setName(next.mode === "rename" ? next.category.name : "");
    setCode(next.mode === "rename" ? next.category.code ?? "" : "");
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    if (name.trim().length < 2) return toast.error("Informe o nome.");

    setSaving(true);
    try {
      if (editing.mode === "rename") {
        await updateCategoryApi(token, editing.category.id, { name: name.trim(), code: code.trim() || null });
      } else {
        await createCategoryApi(token, {
          name: name.trim(),
          code: code.trim() || null,
          ...(editing.mode === "root" ? { kind: editing.kind } : { parentId: editing.parent.id }),
        });
      }
      toast.success("Categoria salva.");
      setEditing(null);
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a categoria.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (category: FinCategory) => {
    try {
      await updateCategoryApi(token, category.id, { isActive: !category.isActive });
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a categoria.");
    }
  };

  const remove = async () => {
    if (!removing) return;
    setSaving(true);
    try {
      await deleteCategoryApi(token, removing.id);
      toast.success("Categoria excluída.");
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir a categoria.");
    } finally {
      setRemoving(null);
      setSaving(false);
    }
  };

  const renderNode = (node: FinCategory, isChild: boolean) => (
    <li key={node.id}>
      <div className={`finTree__node ${isChild ? "finTree__node--child" : ""}`}>
        <span
          className={`finTree__name ${!isChild ? "finTree__name--group" : ""} ${
            node.isActive ? "" : "finTree__name--inactive"
          }`}
        >
          {node.code ? <span className="finTree__code">{node.code}</span> : null}
          <span className="finTree__label">{node.name}</span>
          {node.isSystem ? (
            <span className="finTag" title="Categoria do sistema: usada pelas integrações, não pode ser excluída">
              sistema
            </span>
          ) : null}
          {!node.isActive ? <span className="finTag finTag--off">inativa</span> : null}
        </span>
        {canWrite ? (
          // Quatro colunas fixas: botão ausente vira um espaço vazio, para os
          // botões ficarem alinhados de uma linha para a outra.
          <span className="finTree__actions">
            {!isChild ? (
              <button type="button" className="finGhostBtn finGhostBtn--primary" onClick={() => open({ mode: "child", parent: node })}>
                + Sub
              </button>
            ) : (
              <span aria-hidden="true" />
            )}
            <button type="button" className="finGhostBtn" onClick={() => open({ mode: "rename", category: node })}>
              Renomear
            </button>
            <button type="button" className="finGhostBtn" onClick={() => toggleActive(node)}>
              {node.isActive ? "Inativar" : "Reativar"}
            </button>
            {!node.isSystem ? (
              <button type="button" className="finGhostBtn finGhostBtn--danger" onClick={() => setRemoving(node)}>
                Excluir
              </button>
            ) : (
              <span aria-hidden="true" />
            )}
          </span>
        ) : null}
      </div>
      {node.children.length > 0 ? (
        <ul className="finTree">{node.children.map((child) => renderNode(child, true))}</ul>
      ) : null}
    </li>
  );

  return (
    <div className="dpeSection">
      <p className="finHint">
        Lance sempre numa subcategoria; os grupos somam as filhas nos filtros. Categoria já usada não se exclui: inative
        para tirá-la das escolhas sem perder o histórico.
      </p>

      {!tree ? (
        <p className="ecoEmpty">Carregando...</p>
      ) : (
        <div className="finTreeCols">
          {(["DESPESA", "RECEITA"] as FinCategoryKind[]).map((kind) => (
            <section key={kind} className={`ecoCard finTreeCard finTreeCard--${kind}`}>
              <div className="ecoCard__header finTreeCard__header">
                <h3 className="ecoCard__title">{KIND_TITLES[kind]}</h3>
                {canWrite ? (
                  <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => open({ mode: "root", kind })}>
                    Novo grupo
                  </button>
                ) : null}
              </div>
              <ul className="finTree">{tree.filter((n) => n.kind === kind).map((n) => renderNode(n, false))}</ul>
            </section>
          ))}
        </div>
      )}

      {editing ? (
        <div className="dpModalOverlay">
          <div className="dpModal" role="dialog" aria-modal="true" aria-label="Categoria">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {editing.mode === "rename"
                  ? "Renomear categoria"
                  : editing.mode === "root"
                    ? `Novo grupo de ${KIND_TITLES[editing.kind].toLowerCase()}`
                    : `Nova subcategoria em ${editing.parent.name}`}
              </h2>
              <button
                type="button"
                className="dpModal__close"
                onClick={() => setEditing(null)}
                disabled={saving}
                aria-label="Fechar"
              >
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                <div className="dpFormGrid">
                  <div className="dpFormGroup">
                    <label htmlFor="catName">Nome *</label>
                    <input
                      id="catName"
                      className="dpInput"
                      maxLength={80}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={saving}
                      autoFocus
                    />
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="catCode">Código</label>
                    <input
                      id="catCode"
                      className="dpInput"
                      maxLength={20}
                      placeholder="Ex.: 4.06"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setEditing(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={removing !== null}
        title="Excluir categoria?"
        message={`"${removing?.name ?? ""}" será excluída. Só é possível sem subcategorias e sem lançamentos.`}
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
